import { useState } from 'react';
import { useMutation } from 'react-query';
import { api } from '../../services/api';
import Modal from '../ui/Modal';
import Button from '../ui/Button';
import { ErrorNotice } from '../ui/States';
import { errorMessage, inputClass } from '../../lib/programs';
import { EventView, dayLabel } from '../../lib/events';
import { alignedStart } from '../../lib/eventInsights';

const nextYear = (day: string) => `${Number(day.slice(0, 4)) + 1}${day.slice(4)}`;

/** A new event from a past one: days, venue, rules, audiences, checklist and agenda skeleton; no people. */
export default function StartNextYear({
  event,
  onClose,
  onCreated,
}: {
  event: EventView;
  onClose: () => void;
  onCreated: (id: string) => void;
}) {
  const first = event.days[0].date;
  const [title, setTitle] = useState(event.title.replace(/\b(20\d\d)\b/, (y) => String(Number(y) + 1)));
  const [titleFr, setTitleFr] = useState((event.titleFr ?? '').replace(/\b(20\d\d)\b/, (y) => String(Number(y) + 1)));
  const [startDate, setStartDate] = useState(nextYear(first));
  const [withSpeakers, setWithSpeakers] = useState(false);
  const valid = /^\d{4}-\d{2}-\d{2}$/.test(startDate) && !Number.isNaN(Date.parse(startDate));
  const start = valid ? alignedStart(first, startDate) : null;
  const offset = (d: string) => Math.round((Date.parse(`${d}T12:00:00Z`) - Date.parse(`${first}T12:00:00Z`)) / 864e5);
  const shifted = start
    ? event.days.map((d) => {
        const x = new Date(`${start}T12:00:00Z`);
        x.setUTCDate(x.getUTCDate() + offset(d.date));
        return x.toISOString().slice(0, 10);
      })
    : [];
  const save = useMutation(
    async () =>
      (await api.post(`/events/${event.id}/clone`, { title, titleFr: titleFr || null, startDate, withSpeakers })).data
        .data,
    {
      onSuccess: (d) => onCreated(d.event.id),
    },
  );
  return (
    <Modal
      title="Start next year's event from this one"
      onClose={onClose}
      width={620}
      footer={
        <>
          <Button variant="primary" disabled={!title || !valid || save.isLoading} onClick={() => save.mutate()}>
            {save.isLoading ? 'Creating…' : 'Create the event'}
          </Button>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
        </>
      }
    >
      <div className="grid grid-cols-2 gap-4">
        <div className="col-span-2">
          <label className="block text-small text-ink-2 mb-1">Title (English)</label>
          <input className={inputClass} value={title} onChange={(e) => setTitle(e.target.value)} />
        </div>
        <div className="col-span-2">
          <label className="block text-small text-ink-2 mb-1">Title (French)</label>
          <input className={inputClass} value={titleFr} onChange={(e) => setTitleFr(e.target.value)} />
        </div>
        <div>
          <label className="block text-small text-ink-2 mb-1">First day (about)</label>
          <input type="date" className={inputClass} value={startDate} onChange={(e) => setStartDate(e.target.value)} />
        </div>
      </div>
      <label className="flex items-center gap-2 text-small mt-4">
        <input type="checkbox" checked={withSpeakers} onChange={(e) => setWithSpeakers(e.target.checked)} />
        Also copy this year&rsquo;s speakers (not those who declined) as &ldquo;to confirm&rdquo;, with their sessions
      </label>
      {start ? (
        <p className="text-small mt-4">
          Days: {shifted.map((d) => dayLabel(d)).join(', ')}
          {start !== startDate ? (
            <span className="text-ink-2"> (moved to the same weekday as {dayLabel(first)})</span>
          ) : null}
        </p>
      ) : null}
      <p className="text-small text-ink-2 mt-3">
        Copied: kind, mode, venue and city, the days pattern with labels, times and dinners, travel rules, capacity and
        hotel block (dates moved), the audiences (who is invited, their days and whether travel is covered), the
        reply-to address, the planning checklist and the agenda&rsquo;s sessions (speakers only when ticked, as
        &ldquo;to confirm&rdquo;, without statuses, honoraria or notes). Never copied: invitees, needs, check-ins,
        attendance, payments, form links, the Zoom webinar or documents.
      </p>
      {save.error ? (
        <div className="mt-3">
          <ErrorNotice>{errorMessage(save.error, 'Could not create the event.')}</ErrorNotice>
        </div>
      ) : null}
    </Modal>
  );
}
